<script setup>
import { computed, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import Loading from '../components/Loading.vue'
import {
  KkTextarea,
  KkTextField,
  KkValidationError,
  KkFormLabel,
  KkButton
} from '../components/base'
import { KkInfo } from '../components/ui'
import Box from '../components/Box'
import { mapionSdkOrigin } from '../modules/mapionSdkOrigin'
import { boundaryOfJapanMap, isValidLatitude, isValidLongitude } from '../plugins/vee-validate'

const store = useStore()
const router = useRouter()
const { t } = useI18n()

const emit = defineEmits(['update-title'])
const mapionBizAttributes = computed(() => store.state.companiesStoreOcMapionBizAttributes.data)
const mapionBizAttributesLoading = computed(() => store.state.companiesStoreOcMapionBizAttributes.loading)
const storeAttributes = computed(() => store.state.companiesLocationStore.data)
const storeLoading = computed(() => store.state.companiesLocationStore.loading)
const loading = computed(() => storeLoading.value || mapionBizAttributesLoading.value )
const readStoreOcMapionBizAttributes = () => store.dispatch('companiesStoreOcMapionBizAttributes/readStoreOcMapionBizAttributes')
const updateStoreOcMapionBizAttributes = (payload) => store.dispatch('companiesStoreOcMapionBizAttributes/updateStoreOcMapionBizAttributes', payload)
const readStore = ({ company_uuid, store_uuid }) => store.dispatch('companiesLocationStore/readStore', { company_uuid, store_uuid })
const { store_uuid, company_uuid } = useRoute().params

const state = reactive({
  map: null,
  marker: null,
})

const schema = {
  latitude: 'required|latitude',
  longitude: 'required|longitude',
  extraName: 'max:100',
  notice: 'max:500',
  regularHourDescription: 'max:100',
}

const { handleSubmit, meta, setFieldValue, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    latitude: 0,
    longitude: 0,
    extraName: '',
    notice: '',
    regularHourDescription: ''
  },
})

const { value: latitude, errorMessage: latitudeError, handleChange: latitudeHandleChange } = useField('latitude', undefined, { label: t('pages.CompaniesLocationStoreMapionBizEdit.latitude') })
const { value: longitude, errorMessage: longitudeError, handleChange: longitudeHandleChange } = useField('longitude', undefined, { label: t('pages.CompaniesLocationStoreMapionBizEdit.longitude') })
const { value: extraName, errorMessage: extraNameError, handleChange: extraNameHandleChange } = useField('extraName', undefined, { label: t('pages.CompaniesLocationStoreMapionBizEdit.profile.extraName') })
const { value: notice, errorMessage: noticeError, handleChange: noticeHandleChange } = useField('notice', undefined, { label: t('pages.CompaniesLocationStoreMapionBizEdit.profile.notice') })
const { value: regularHourDescription, errorMessage: regularHourDescriptionError, handleChange: regularHourDescriptionHandleChange } = useField('regularHourDescription', undefined, { label: t('pages.CompaniesLocationStoreMapionBizEdit.profile.regularHourDescription') })

watch(
  [latitude, longitude],
  (geoValue) => {
    const [latitude, longitude] = geoValue
    if(!state.map) setMap()
    if(latitude && longitude) {
      const point = L.latLng(latitude, longitude)
      if(!state.marker) setMarker()

      if(isValidLatitude(point.lat) && isValidLongitude(point.lng)) {
        state.marker.setLatLng(point)
        state.map.panTo(point)
      }
    }
    state.map.invalidateSize()
  }
)

const initData = () => {
  setValues({
    latitude: mapionBizAttributes.value.latitude ?? 0,
    longitude: mapionBizAttributes.value.longitude ?? 0,
    extraName: mapionBizAttributes.value.profile.extraName ?? '',
    notice: mapionBizAttributes.value.profile.notice ?? '',
    regularHourDescription:  mapionBizAttributes.value.profile.regularHourDescription ?? '',
  })
}

const handleUpdateAttributes = handleSubmit(async (value) => {
  const payload = {
    storeOcMapionBizAttributes: {
      latitude: value.latitude,
      longitude: value.longitude,
      profile: {
        extraName: value.extraName,
        notice: value.notice,
        regularHourDescription: value.regularHourDescription,
      }
    }
  }
  const isSuccess = await updateStoreOcMapionBizAttributes(payload)
  if(isSuccess) router.push({ name: 'CompaniesLocationStore', params: { company_uuid: company_uuid, store_uuid: store_uuid } })
})

const setMap = () => {
  state.map = window.L.map.mapion('map', {
    center: L.latLng(35.6809591, 139.7673068),
    maxBounds: [
      [boundaryOfJapanMap.northwest.latitude, boundaryOfJapanMap.northwest.longitude], // 左上
      [boundaryOfJapanMap.southeast.latitude, boundaryOfJapanMap.southeast.longitude] // 右下
    ],
    zoom: 18
  })
  state.map.invalidateSize()
}

const setMarker = () => {
  state.marker = L.marker(L.latLng(35.6809591, 139.7673068), {draggable: true})
  state.marker.on('dragend', (event) => {
    const _marker = event.target
    const _point = _marker.getLatLng()

    setFieldValue('latitude', _point.lat)
    setFieldValue('longitude', _point.lng)
  })
  state.map.addLayer(state.marker)
}

const beforeLoad = ()=> {
  const scriptEl = document.createElement('script')
  scriptEl.setAttribute('src', `${ mapionSdkOrigin }/maps/api/js?key=mov_lone_map&v=1.3&dtm=jgd`)
  const linkLeaflet = document.createElement('link')
  linkLeaflet.setAttribute('href', `${ mapionSdkOrigin }/maps/api/v1.3/leaflet.css`)
  linkLeaflet.setAttribute('rel', 'stylesheet')
  const linkMapion = document.createElement('link')
  linkMapion.setAttribute('href', `${ mapionSdkOrigin }/maps/api/v1.3/mapion-plugin.css`)
  linkMapion.setAttribute('rel', 'stylesheet')
  document.head.appendChild(scriptEl)
  document.head.appendChild(linkLeaflet)
  document.head.appendChild(linkMapion)
}

beforeLoad()

const load = async () => {
  await Promise.all([
    readStoreOcMapionBizAttributes(),
    readStore({ company_uuid: company_uuid, store_uuid: store_uuid }),
  ])
  emit('update-title', t('pages.CompaniesLocationStoreMapionBizEdit.title', { name: storeAttributes.value.attributes.name }))
  initData()
  if(!state.map) setMap()
  if(!state.marker) setMarker()
}

load()
</script>
<template>
  <div>
    <Loading v-if="loading" />
    <form v-else class="p-box" @submit.prevent="handleUpdateAttributes">
      <Box>
        <template #header>
          <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStoreMapionBizEdit.editTitle') }}</h3>
        </template>
        <template #body>
          <!-- 所在地点 -->
          <div class="container">
            <div class="geoContainer">
              <div id="map" />
              <div class="geoContainer__form">
                <!-- 緯度 -->
                <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreMapionBizEdit.latitude') }${ $t('labels.requiredLabel', { count: latitude.length }) }`" />
                <KkTextField :model-value="latitude" class="c-textField" :placeholder="$t('messages.noSettings')" @update:model-value="latitudeHandleChange" />
                <KkValidationError :text="latitudeError" />
                <!-- 経度 -->
                <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreMapionBizEdit.longitude') }${ $t('labels.requiredLabel', { count: longitude.length }) }`" />
                <KkTextField :model-value="longitude" class="c-textField" :placeholder="$t('messages.noSettings')" @update:model-value="longitudeHandleChange" />
                <KkValidationError :text="longitudeError" />
              </div>
            </div>
          </div>
          <!-- 店舗名補足 -->
          <div class="container">
            <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreMapionBizEdit.profile.extraName') }(${ $t('labels.countCharacters', { count: extraName.replace(/\n|\s/g, '').length, max: 100 }) })${ $t('labels.optionalLabel', { count: extraName.length }) }`" />
            <KkTextarea v-model="extraName" @change="extraNameHandleChange" />
            <KkValidationError :text="extraNameError" />
            <KkInfo class="container__info">
              {{ $t('pages.CompaniesLocationStoreMapionBizEdit.profile.extraNameInfo') }}
            </KkInfo>
          </div>
          <!-- 店舗からのお知らせ -->
          <div class="container">
            <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreMapionBizEdit.profile.notice') }(${ $t('labels.countCharacters', { count: notice.replace(/\n|\s/g, '').length, max: 500 }) })${ $t('labels.optionalLabel', { count: notice.length }) }`" />
            <KkTextarea v-model="notice" @change="noticeHandleChange" />
            <KkValidationError :text="noticeError" />
          </div>
          <!-- 営業時間の補足 -->
          <div class="container">
            <KkFormLabel :text="`${ $t('pages.CompaniesLocationStoreMapionBizEdit.profile.regularHourDescription') }(${ $t('labels.countCharacters', { count: regularHourDescription.replace(/\n|\s/g, '').length, max: 100 }) })${ $t('labels.optionalLabel', { count: regularHourDescription.length }) }`" />
            <KkTextarea v-model="regularHourDescription" @change="regularHourDescriptionHandleChange" />
            <KkValidationError :text="regularHourDescriptionError" />
          </div>
        </template>
      </Box>
      <section class="p-box">
        <div class="p-box__footer">
          <div class="buttonGroup">
            <KkButton :to="{ name: 'CompaniesLocationStore', params: { company_uuid: company_uuid, store_uuid: store_uuid } }"
                      :text="$t('labels.cancel')"
                      :color="'secondary'"
                      class="buttonGroup__item"
            />
            <KkButton
              color="primary"
              :text="$t('labels.save')"
              :disabled="!meta.valid"
              class="buttonGroup__item"
              type="submit"
            />
          </div>
        </div>
      </section>
    </form>
  </div>
</template>
<style lang="scss" scoped>
.container {
  padding: 24px 24px 50px;
  margin-bottom: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  .info {
    margin-top: 24px;
  }
}
.geoContainer {
  display: flex;
  justify-content: space-around;
  &__form {
    width: 50%;
  }
  #map {
    position: relative;
    width: 40%;
    height: 400px;
  }
}
.buttonGroup {
  display: flex;
  flex-grow: 0;
  justify-content: flex-end;

  &__item {
    margin-left: 16px
  }
}
</style>
